import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline'
import { formatMeterCents } from '../../utils/gamingCurrency'

export function formatDateTime(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString()
}

/** Human-readable coverage window from ISO record timestamps. */
export function formatRecordTimeRange(first, last, { emptyLabel = '—' } = {}) {
  if (!first && !last) return emptyLabel
  if (first && last) return `${formatDateTime(first)} → ${formatDateTime(last)}`
  return formatDateTime(first || last)
}

export function formatTradingDay(iso) {
  if (!iso) return '—'
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatBytes(n) {
  if (n == null || n === 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export function formatNumber(n) {
  if (n == null) return '—'
  return Number(n).toLocaleString()
}

/** Meter counter totals/deltas (stored in cents) → AUD display with cents. */
export function formatMeterRaw(value) {
  return formatMeterCents(value)
}

const IMPORT_MODE_LABELS = {
  single_file: 'Single Azure file',
  all_batches: 'All batches for trading day',
  yesterday: 'Yesterday (scheduled offset)',
  trading_day_single: 'Best match for trading day',
  api: 'RXConnect API',
  file: 'File extract',
}

export function importModeLabel(mode) {
  return IMPORT_MODE_LABELS[mode] || mode || '—'
}

export function fileIngestBadge(status) {
  if (status === 'ingested') return statusBadge('ingested')
  if (status === 'failed') return statusBadge('failed')
  return statusBadge('not_ingested')
}

export function statusBadge(status) {
  const map = {
    ingested: { cls: 'bg-green-100 text-green-800', label: 'Ingested', Icon: CheckCircleIcon },
    success: { cls: 'bg-green-100 text-green-800', label: 'Success', Icon: CheckCircleIcon },
    missing: { cls: 'bg-amber-100 text-amber-800', label: 'Not ingested', Icon: ExclamationTriangleIcon },
    not_ingested: { cls: 'bg-amber-100 text-amber-800', label: 'Not ingested', Icon: ExclamationTriangleIcon },
    failed: { cls: 'bg-red-100 text-red-800', label: 'Failed', Icon: XCircleIcon },
    partial: { cls: 'bg-yellow-100 text-yellow-800', label: 'Partial', Icon: ExclamationTriangleIcon },
    skipped: { cls: 'bg-gray-100 text-gray-700', label: 'Skipped', Icon: MinusCircleIcon },
    disabled: { cls: 'bg-gray-100 text-gray-600', label: 'Disabled', Icon: MinusCircleIcon },
    coming_soon: { cls: 'bg-slate-100 text-slate-600', label: 'Coming soon', Icon: ClockIcon },
    running: { cls: 'bg-blue-100 text-blue-800', label: 'Running', Icon: ClockIcon },
    waiting_ingest: { cls: 'bg-amber-100 text-amber-800', label: 'Waiting ingest', Icon: ExclamationTriangleIcon },
    ingest_ok: { cls: 'bg-blue-100 text-blue-800', label: 'Ingest OK', Icon: ClockIcon },
    enrich_failed: { cls: 'bg-red-100 text-red-800', label: 'Enrich failed', Icon: XCircleIcon },
    allocate_failed: { cls: 'bg-red-100 text-red-800', label: 'Allocate failed', Icon: XCircleIcon },
    rules_ready: { cls: 'bg-green-100 text-green-800', label: 'Rules ready', Icon: CheckCircleIcon },
    stale: { cls: 'bg-yellow-100 text-yellow-800', label: 'Stale', Icon: ExclamationTriangleIcon },
  }
  const entry = map[status] || { cls: 'bg-gray-100 text-gray-700', label: status || 'Unknown', Icon: MinusCircleIcon }
  const { cls, label, Icon } = entry
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  )
}

export function feedStatusColor(status) {
  switch (status) {
    case 'ingested': return 'border-green-200 bg-green-50'
    case 'failed': return 'border-red-200 bg-red-50'
    case 'missing': return 'border-amber-200 bg-amber-50'
    case 'disabled': return 'border-gray-200 bg-gray-50'
    case 'coming_soon': return 'border-slate-200 bg-slate-50 opacity-75'
    default: return 'border-gray-200 bg-white'
  }
}

/** Classify import outcome and build a user-visible summary (meter / sessions / batch). */
export function buildGamingImportResultMessage(d, options = {}) {
  const { feedLabel = 'Import', batch, tradingDay } = options
  const imported = batch ? batch.imported : (d?.imported ?? 0)
  const skipped = batch ? batch.skipped : (d?.skipped ?? 0)
  const errors = batch ? batch.errors : (d?.errors ?? 0)
  const updated = batch ? (batch.updated ?? 0) : (d?.updated ?? 0)
  const duration_ms = d?.duration_ms
  const trading_day = d?.trading_day || tradingDay || null

  let type = 'success'
  if (errors > 0 && imported === 0 && updated === 0) type = 'error'
  else if (errors > 0 || batch?.stopped) type = 'warning'
  else if (imported === 0) type = 'warning'

  const errorDetails = d?.error_details?.length
    ? d.error_details
    : (d?.error_message ? String(d.error_message).split('\n').filter(Boolean) : [])

  let summary
  if (batch) {
    const { files_done, files_total, stopped } = batch
    summary = stopped
      ? `Batch stopped — ${files_done}/${files_total} files: ${imported} imported, ${skipped} skipped, ${errors} errors`
      : `Batch complete — ${files_total} files: ${imported} imported, ${skipped} skipped, ${errors} errors`
    if (imported === 0 && errors === 0 && !stopped) {
      summary += ' (no new rows imported)'
    }
  } else {
    const dayPart = trading_day ? ` for ${formatTradingDay(trading_day)}` : ''
    if (type === 'error') {
      summary = `${feedLabel} failed${dayPart} — ${errors} error(s), nothing imported (${duration_ms ?? 0}ms)`
    } else if (imported === 0 && errors === 0) {
      summary = `${feedLabel} finished${dayPart} — no rows imported (${skipped} skipped, ${duration_ms ?? 0}ms)`
    } else {
      summary = `${feedLabel} complete${dayPart} — ${imported} imported, ${skipped} skipped, ${errors} errors (${duration_ms ?? 0}ms)`
      if (options.showDenomSync && updated > 0) {
        summary += ` · ${updated} denoms synced`
      }
    }
  }

  const turnover = d?.ingest_summary?.reconciliation?.totals?.turnover
    ?? d?.ingest_summary?.totals?.turnover
  if (turnover != null && Number.isFinite(Number(turnover))) {
    summary += ` · Turnover Δ ${formatMeterCents(turnover)}`
  }

  const timeRange = formatRecordTimeRange(d?.record_time_first, d?.record_time_last, { emptyLabel: '' })
  if (timeRange) {
    summary += ` · Records ${timeRange}`
  }

  return { type, summary, errorDetails, trading_day }
}

/** Normalise error_details / error_message from an import run or API payload. */
export function parseImportErrorDetails(source) {
  if (!source) return []
  const raw = source.error_details
  if (Array.isArray(raw) && raw.length) return raw.map(String)
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.map(String)
    } catch {
      /* fall through */
    }
  }
  if (source.error_message) {
    return String(source.error_message).split('\n').filter(Boolean)
  }
  return []
}

export function ImportErrorCount({ count, onClick, className = '' }) {
  if (count == null || count <= 0) return '—'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-red-700 font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer ${className}`}
      title="View error details"
    >
      {count}
    </button>
  )
}

export function ImportResultBanner({ message, onDismiss, className = '' }) {
  if (!message) return null
  const borderCls = message.type === 'success'
    ? 'bg-green-50 border-green-300 text-green-900'
    : message.type === 'warning'
      ? 'bg-amber-50 border-amber-300 text-amber-950'
      : message.type === 'info'
        ? 'bg-sky-50 border-sky-300 text-sky-900'
        : 'bg-red-50 border-red-300 text-red-900'

  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-lg border-2 p-4 text-sm shadow-sm ${borderCls} ${className}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-medium">{message.summary || message.text}</p>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="text-xs underline opacity-70 hover:opacity-100 shrink-0">
            Dismiss
          </button>
        )}
      </div>
      {message.hint && (
        <div className="mt-1 text-xs opacity-90">{message.hint}</div>
      )}
      {(message.errorDetails?.length > 0) && (
        <div className="mt-3 pt-3 border-t border-current/20">
          <p className="font-medium mb-1">Details</p>
          <ul className="list-disc list-inside space-y-0.5 font-mono text-xs opacity-90 max-h-40 overflow-y-auto">
            {message.errorDetails.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
